import React, { useState, useRef, useEffect, useCallback } from "react";
import { generateShareText, MAX_GUESSES, ITEMS_PER_DAY } from "../utils/gameHelpers";

export default function ShareButton({ slotStates, dailyItems, todayStr, hardModeSlots }) {
  const [copied, setCopied] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelTab, setPanelTab] = useState("text"); // "text" | "image"
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [imageSaved, setImageSaved] = useState(false);
  const panelRef = useRef(null);

  // Close panel on outside click
  useEffect(() => {
    if (!panelOpen) return;
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setPanelOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [panelOpen]);

  const generateCanvas = useCallback(() => {
    const canvas = document.createElement("canvas");
    const w = 400;
    const rowH = 40;
    const headerH = 80;
    const footerH = 40;
    const h = headerH + dailyItems.length * rowH + footerH;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#f8f1e4";
    ctx.fillRect(0, 0, w, h);

    ctx.fillStyle = "#3e2c1c";
    ctx.font = "bold 22px serif";
    ctx.textAlign = "center";
    ctx.fillText("Erewhondle", w / 2, 32);

    const wins = Object.values(slotStates).filter((s) => s.status === "won").length;
    const totalGuesses = Object.values(slotStates).reduce((sum, s) => sum + s.guesses.length, 0);
    ctx.font = "14px serif";
    ctx.fillStyle = "#5a4c3b";
    ctx.fillText(`${todayStr}  •  ${wins}/${ITEMS_PER_DAY} correct  •  ${totalGuesses} guesses`, w / 2, 58);

    const colors = {
      correct: "#3da35d",
      high: "#ffc107",
      low: "#ffc107",
      "way high": "#dc3545",
      "way low": "#dc3545",
    };
    const boxSize = 28;
    const gap = 4;
    const iconWidth = 28;
    const rowStartX = 20; // left-aligned

    for (let i = 0; i < dailyItems.length; i++) {
      const d = dailyItems[i];
      const state = slotStates[d.slot];
      if (!state) continue;
      const y = headerH + i * rowH;
      const isHard = hardModeSlots?.[d.slot];

      ctx.font = "18px serif";
      ctx.textAlign = "left";
      ctx.fillStyle = "#3e2c1c";
      const icon = state.status === "won" ? (isHard ? "🔥" : "✓") : "✗";
      ctx.fillText(icon, rowStartX, y + 24);

      const startX = rowStartX + iconWidth + 8;
      for (let j = 0; j < MAX_GUESSES; j++) {
        const g = state.guesses[j];
        const x = startX + j * (boxSize + gap);
        if (g) {
          ctx.fillStyle = colors[g.feedback] || "#ccc";
          ctx.beginPath();
          ctx.roundRect(x, y + 4, boxSize, boxSize, 4);
          ctx.fill();
        } else {
          ctx.strokeStyle = "#ccc";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.roundRect(x, y + 4, boxSize, boxSize, 4);
          ctx.stroke();
        }
      }
    }

    ctx.font = "12px serif";
    ctx.fillStyle = "#7a6a58";
    ctx.textAlign = "center";
    ctx.fillText("erewhondle.com", w / 2, h - 14);

    return canvas;
  }, [slotStates, dailyItems, todayStr, hardModeSlots]);

  // Generate image preview when switching to image tab
  useEffect(() => {
    if (panelOpen && panelTab === "image" && !imageDataUrl) {
      const canvas = generateCanvas();
      setImageDataUrl(canvas.toDataURL("image/png"));
    }
  }, [panelOpen, panelTab, imageDataUrl, generateCanvas]);

  // Reset when panel closes
  useEffect(() => {
    if (!panelOpen) {
      setImageDataUrl(null);
      setImageSaved(false);
    }
  }, [panelOpen]);

  const shareText = generateShareText(slotStates, dailyItems, todayStr, hardModeSlots);

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      const textarea = document.createElement("textarea");
      textarea.value = shareText;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShareText = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText });
      } catch (e) { /* user cancelled */ }
    }
  };

  const handleSaveImage = () => {
    const canvas = generateCanvas();
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `erewhondle-${todayStr}.png`;
      a.click();
      URL.revokeObjectURL(url);
      setImageSaved(true);
      setTimeout(() => setImageSaved(false), 2000);
    }, "image/png");
  };

  const handleNativeShareImage = async () => {
    const canvas = generateCanvas();
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], "erewhondle.png", { type: "image/png" });
      const shareData = { files: [file] };
      if (navigator.canShare?.(shareData)) {
        try {
          await navigator.share(shareData);
        } catch (e) { /* user cancelled */ }
      } else {
        handleSaveImage();
      }
    }, "image/png");
  };

  const canNativeShare = typeof navigator.share === "function";

  return (
    <div className="share-wrapper" ref={panelRef}>
      <button className="share-btn" onClick={() => setPanelOpen((o) => !o)}>
        {copied ? "Copied!" : "Share"}
      </button>

      {panelOpen && (
        <div className="share-panel">
          <div className="share-tabs">
            <button
              className={`share-tab ${panelTab === "text" ? "active" : ""}`}
              onClick={() => setPanelTab("text")}
            >
              📋 Text
            </button>
            <button
              className={`share-tab ${panelTab === "image" ? "active" : ""}`}
              onClick={() => setPanelTab("image")}
            >
              🖼️ Image
            </button>
          </div>

          {panelTab === "text" && (
            <div className="share-preview-text">
              <pre className="share-text-content">{shareText}</pre>
              <div className="share-actions">
                <button className="share-action-btn" onClick={handleCopyText}>
                  {copied ? "✓ Copied" : "Copy"}
                </button>
                {canNativeShare && (
                  <button className="share-action-btn share-action-send" onClick={handleNativeShareText}>
                    Send
                  </button>
                )}
              </div>
              <div className="share-social-row">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-social-btn share-social-x"
                  title="Share on X"
                >
                  𝕏
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://erewhondle.com")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-social-btn share-social-fb"
                  title="Share on Facebook"
                >
                  f
                </a>
              </div>
            </div>
          )}

          {panelTab === "image" && (
            <div className="share-preview-image">
              {imageDataUrl ? (
                <img src={imageDataUrl} alt="Share preview" className="share-image-preview" />
              ) : (
                <div className="share-image-loading">Generating...</div>
              )}
              <div className="share-actions">
                <button className="share-action-btn" onClick={handleSaveImage}>
                  {imageSaved ? "✓ Saved" : "Save"}
                </button>
                {canNativeShare && (
                  <button className="share-action-btn share-action-send" onClick={handleNativeShareImage}>
                    Send
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
